/**
 * Publisher API toggle — data layer (FS-154 / DASH).
 *
 * Typed wrappers over `apiClient` for the admin-facing toggle endpoints
 * exposed by `/api/v1/publisher-api-toggle`:
 *
 *   GET  /_scopes                        — catalog of toggle-able surfaces.
 *   GET  /                                — list all toggles for this tenant.
 *   GET  /:affiliate_id                   — fetch one toggle state (zero-row
 *                                           placeholder when never provisioned).
 *   PUT  /:affiliate_id                   — upsert full state.
 *   POST /:affiliate_id/enable            — idempotent flip on.
 *   POST /:affiliate_id/disable           — idempotent flip off (revoke).
 *   GET  /:affiliate_id/audit             — append-only toggle history.
 *
 * This module is intentionally React-free so the page test can exercise the
 * validators and fetch surface without a DOM. The page itself drives a
 * small useState pattern mirroring PublisherApiPage.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Scope catalog — mirrors the API's AVAILABLE_SCOPES tuple. Hand-rolled here
// so the dash test does not need to import from the worker tree.
// ---------------------------------------------------------------------------

export const AVAILABLE_SCOPES = [
	"campaigns",
	"stats",
	"creatives",
	"payouts",
	"conversions",
] as const;
export type ApiScope = (typeof AVAILABLE_SCOPES)[number];

export function isValidScope(s: unknown): s is ApiScope {
	return (
		typeof s === "string" &&
		(AVAILABLE_SCOPES as readonly string[]).includes(s)
	);
}

// ---------------------------------------------------------------------------
// Response shapes.
// ---------------------------------------------------------------------------

export interface PublisherApiToggle {
	id: string;
	tenant_id: string;
	affiliate_id: string;
	enabled: boolean;
	scopes: ApiScope[];
	rate_limit_per_min: number | null;
	note: string | null;
	updated_by: string | null;
	created_at: number;
	updated_at: number;
}

export interface ToggleSingleResponse {
	toggle: PublisherApiToggle;
	provisioned: boolean;
}

export interface ToggleListResponse {
	toggles: PublisherApiToggle[];
}

export interface ToggleFlipResponse {
	toggle: PublisherApiToggle;
	action: "enable" | "disable" | "update";
	provisioned: boolean;
}

export interface ToggleAuditEntry {
	id: string;
	affiliate_id: string;
	action: string;
	prev_enabled: boolean | null;
	next_enabled: boolean | null;
	prev_scopes: ApiScope[] | null;
	next_scopes: ApiScope[] | null;
	prev_rate_limit: number | null;
	next_rate_limit: number | null;
	actor: string | null;
	note: string | null;
	created_at: number;
}

export interface ToggleAuditResponse {
	affiliate_id: string;
	entries: ToggleAuditEntry[];
}

export interface ToggleScopesResponse {
	scopes: ApiScope[];
}

// ---------------------------------------------------------------------------
// Form validation — mirrors the API-side `PUT` body schema.
// ---------------------------------------------------------------------------

const AFFILIATE_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const NOTE_MAX = 500;
const RATE_LIMIT_MAX = 1_000_000;

export interface UpsertFormInput {
	affiliateId: string;
	enabled: boolean;
	scopes: string[];
	rateLimitPerMin?: number | null | string;
	note?: string | null;
	updatedBy?: string | null;
}

export interface UpsertFormData {
	affiliate_id: string;
	enabled: boolean;
	scopes: ApiScope[];
	rate_limit_per_min: number | null;
	note: string | null;
	updated_by: string | null;
}

export interface UpsertParseOk {
	success: true;
	data: UpsertFormData;
}
export interface UpsertParseErr {
	success: false;
	errors: Record<string, string>;
}
export type UpsertParseResult = UpsertParseOk | UpsertParseErr;

export const upsertToggleSchema = {
	safeParse(input: UpsertFormInput): UpsertParseResult {
		const errors: Record<string, string> = {};

		const affiliateRaw =
			typeof input.affiliateId === "string" ? input.affiliateId.trim() : "";
		if (affiliateRaw.length === 0) {
			errors.affiliateId = "Affiliate ID is required.";
		} else if (!AFFILIATE_ID_RE.test(affiliateRaw)) {
			errors.affiliateId =
				"Affiliate ID may only contain letters, numbers, '-' and '_'.";
		}

		if (typeof input.enabled !== "boolean") {
			errors.enabled = "Enabled must be true or false.";
		}

		const scopes: ApiScope[] = [];
		if (!Array.isArray(input.scopes)) {
			errors.scopes = "Scopes must be a list.";
		} else {
			for (const raw of input.scopes) {
				if (!isValidScope(raw)) {
					errors.scopes = `Unknown scope: ${String(raw)}`;
					break;
				}
				if (!scopes.includes(raw)) scopes.push(raw);
			}
		}

		let rate: number | null = null;
		const rlRaw = input.rateLimitPerMin;
		if (rlRaw !== undefined && rlRaw !== null && rlRaw !== "") {
			const asNum =
				typeof rlRaw === "string" ? Number.parseInt(rlRaw, 10) : rlRaw;
			if (
				typeof asNum !== "number" ||
				!Number.isFinite(asNum) ||
				!Number.isInteger(asNum) ||
				asNum <= 0 ||
				asNum > RATE_LIMIT_MAX
			) {
				errors.rateLimitPerMin =
					"Rate limit must be a positive integer (or left blank).";
			} else {
				rate = asNum;
			}
		}

		let note: string | null = null;
		if (typeof input.note === "string") {
			const trimmed = input.note.trim();
			if (trimmed.length > NOTE_MAX) {
				errors.note = `Note must be ${NOTE_MAX} characters or fewer.`;
			} else if (trimmed.length > 0) {
				note = trimmed;
			}
		}

		let actor: string | null = null;
		if (typeof input.updatedBy === "string") {
			const trimmed = input.updatedBy.trim();
			if (trimmed.length > 0) {
				if (!/^[A-Za-z0-9_.-]{1,128}$/.test(trimmed)) {
					errors.updatedBy = "Actor must be alphanumeric (with . _ -).";
				} else {
					actor = trimmed;
				}
			}
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				affiliate_id: affiliateRaw,
				enabled: input.enabled,
				scopes,
				rate_limit_per_min: rate,
				note,
				updated_by: actor,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// API wrappers.
// ---------------------------------------------------------------------------

export async function fetchScopes(): Promise<ApiScope[]> {
	try {
		const res = await apiClient.get<ToggleScopesResponse>(
			"/publisher-api-toggle/_scopes",
		);
		return Array.isArray(res?.scopes)
			? res.scopes.filter(isValidScope)
			: [...AVAILABLE_SCOPES];
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return [...AVAILABLE_SCOPES];
		}
		throw err;
	}
}

export async function fetchToggleList(): Promise<PublisherApiToggle[]> {
	try {
		const res = await apiClient.get<ToggleListResponse>(
			"/publisher-api-toggle",
		);
		return Array.isArray(res?.toggles) ? res.toggles : [];
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return [];
		}
		throw err;
	}
}

export async function fetchToggle(
	affiliateId: string,
): Promise<ToggleSingleResponse | null> {
	const id = affiliateId.trim();
	if (!id) return null;
	try {
		return await apiClient.get<ToggleSingleResponse>(
			`/publisher-api-toggle/${encodeURIComponent(id)}`,
		);
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return null;
		}
		throw err;
	}
}

export async function upsertToggle(
	body: UpsertFormData,
): Promise<ToggleSingleResponse> {
	return apiClient.put<ToggleSingleResponse>(
		`/publisher-api-toggle/${encodeURIComponent(body.affiliate_id)}`,
		{
			enabled: body.enabled,
			scopes: body.scopes,
			rate_limit_per_min: body.rate_limit_per_min,
			note: body.note,
			updated_by: body.updated_by,
		},
	);
}

export async function enableToggle(
	affiliateId: string,
	opts: { note?: string | null; updatedBy?: string | null } = {},
): Promise<ToggleFlipResponse> {
	return apiClient.post<ToggleFlipResponse>(
		`/publisher-api-toggle/${encodeURIComponent(affiliateId)}/enable`,
		{
			note: opts.note ?? null,
			updated_by: opts.updatedBy ?? null,
		},
	);
}

export async function disableToggle(
	affiliateId: string,
	opts: { note?: string | null; updatedBy?: string | null } = {},
): Promise<ToggleFlipResponse> {
	return apiClient.post<ToggleFlipResponse>(
		`/publisher-api-toggle/${encodeURIComponent(affiliateId)}/disable`,
		{
			note: opts.note ?? null,
			updated_by: opts.updatedBy ?? null,
		},
	);
}

export async function fetchAudit(
	affiliateId: string,
): Promise<ToggleAuditEntry[]> {
	const id = affiliateId.trim();
	if (!id) return [];
	try {
		const res = await apiClient.get<ToggleAuditResponse>(
			`/publisher-api-toggle/${encodeURIComponent(id)}/audit`,
		);
		return Array.isArray(res?.entries) ? res.entries : [];
	} catch (err) {
		if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
			return [];
		}
		throw err;
	}
}

// ---------------------------------------------------------------------------
// Presentational helpers.
// ---------------------------------------------------------------------------

export function describeAction(action: string): string {
	switch (action) {
		case "enable":
			return "Enabled";
		case "disable":
			return "Disabled";
		case "update":
			return "Updated";
		default:
			return action;
	}
}

export function describeScope(scope: ApiScope): string {
	switch (scope) {
		case "campaigns":
			return "Campaigns";
		case "stats":
			return "Stats";
		case "creatives":
			return "Creatives";
		case "payouts":
			return "Payouts";
		case "conversions":
			return "Conversions";
		default:
			return scope;
	}
}

export function formatRelative(
	epochSec: number | null | undefined,
	now: number = Date.now(),
): string {
	if (!epochSec) return "never";
	const deltaSec = Math.max(0, Math.floor(now / 1000) - epochSec);
	if (deltaSec < 60) return `${deltaSec}s ago`;
	const mins = Math.floor(deltaSec / 60);
	if (mins < 60) return `${mins}m ago`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	return `${days}d ago`;
}

export function toggleStatusLabel(t: PublisherApiToggle): "enabled" | "disabled" {
	return t.enabled ? "enabled" : "disabled";
}

export function sortTogglesByRecency(
	rows: readonly PublisherApiToggle[],
): PublisherApiToggle[] {
	return [...rows].sort((a, b) => b.updated_at - a.updated_at);
}

export const publisherApiTogglePaths = {
	scopes: "/publisher-api-toggle/_scopes",
	list: "/publisher-api-toggle",
	single: "/publisher-api-toggle/:affiliateId",
	enable: "/publisher-api-toggle/:affiliateId/enable",
	disable: "/publisher-api-toggle/:affiliateId/disable",
	audit: "/publisher-api-toggle/:affiliateId/audit",
} as const;
